import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const UsersPage = ({ panelId }) => {
    const [users, setUsers] = useState([]);
    const [panels, setPanels] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);
    const isManagement = window.location.hostname.startsWith('yonetim');
    const [selectedPanelFilter, setSelectedPanelFilter] = useState(() => panelId || localStorage.getItem('selectedPanelId') || '');

    // 2FA State
    const [show2FAModal, setShow2FAModal] = useState(false);
    const [qrCode, setQrCode] = useState('');
    const [twoFactorToken, setTwoFactorToken] = useState('');
    const [current2FAUser, setCurrent2FAUser] = useState(null);

    // Yeni Kullanıcı Form State'i
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        role: 'user',
        permissions: [],
        panelId: ''
    });

    // İzin verilebilecek sayfalar listesi
    const availablePages = [
        { path: '/admin', label: 'Yatırım Havuzu' },
        { path: '/withdrawal-pool', label: 'Çekim Havuzu' },
        { path: '/withdrawal-request', label: 'Çekim Talebi Al' },
        { path: '/accounts', label: 'Hesaplar' },
        { path: '/users', label: 'Kullanıcılar' },
        { path: '/deliveries', label: 'Teslimler' },
        { path: '/user-activities', label: 'Kullanıcı Hareketleri' },
        { path: '/blacklist', label: 'Black List' }
    ];

    useEffect(() => {
        if (panelId) setSelectedPanelFilter(panelId);
    }, [panelId]);

    useEffect(() => {
        if (isManagement) {
            api.getPanels()
                .then(res => setPanels(res.data || []))
                .catch(err => console.error('Paneller alınamadı:', err));
        }
    }, [isManagement]);

    const fetchUsers = useCallback(async () => {
        try {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            let activePanelId = '';
            if (isManagement) {
                activePanelId = selectedPanelFilter || panelId || '';
            } else {
                activePanelId = currentUser?.panelId || panelId || '';
            }

            const res = await api.getUsers({ panelId: activePanelId });
            let data = res.data || [];

            // Sadece alt paneldeki normal 'user' rolündeki kısıtlı kullanıcılar admin hesaplarını görmesin.
            // Yönetim kullanıcıları (yonetim, master_admin, admin) ve departman adminleri tüm kullanıcıları görebilmelidir.
            const isPrivileged = isManagement || ['yonetim', 'master_admin', 'admin'].includes(currentUser?.role);
            if (!isPrivileged && currentUser?.role !== 'admin') {
                data = data.filter(u => u.role !== 'admin');
            }
            setUsers(data);
        } catch (err) {
            console.error('Kullanıcılar alınamadı:', err);
        }
    }, [panelId, selectedPanelFilter, isManagement]);

    useEffect(() => {
        document.title = 'PayzoPay - Kullanıcılar';
        fetchUsers();
        const interval = setInterval(fetchUsers, 5000);
        return () => clearInterval(interval);
    }, [fetchUsers]);

    const logAction = async (action, details) => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const activePanelId = isManagement ? (selectedPanelFilter || panelId) : (user?.panelId || panelId);
            const username = user ? user.username : 'Bilinmiyor';
            await api.createLog({ username, action, details, panelId: activePanelId });
        } catch (error) {
            console.error('Loglama hatası:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (path) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.includes(path)
                ? prev.permissions.filter(p => p !== path)
                : [...prev.permissions, path];
            return { ...prev, permissions: newPermissions };
        });
    };

    const openAddModal = () => {
        setEditingUserId(null);
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        const defaultPanel = isManagement
            ? (selectedPanelFilter || panelId || (panels[0]?.panelId || ''))
            : (currentUser?.panelId || panelId || '');
        setFormData({
            username: '',
            password: '',
            role: 'user',
            permissions: [],
            panelId: defaultPanel
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
            const finalPanelId = isManagement
                ? (formData.panelId || selectedPanelFilter || panelId)
                : (currentUser?.panelId || panelId);

            if (!finalPanelId) {
                alert('Lütfen kullanıcının ait olacağı departmanı seçin.');
                return;
            }

            if (editingUserId) {
                // Güncelleme İşlemi
                const payload = {
                    ...formData,
                    panelId: finalPanelId
                };
                await api.updateUser(editingUserId, payload);
                logAction('Kullanıcı Güncelleme', `${formData.username} kullanıcısı güncellendi.`);
                alert('Kullanıcı güncellendi.');
            } else {
                // Yeni Kayıt İşlemi
                const payload = {
                    ...formData,
                    panelId: finalPanelId,
                    createdBy: currentUser ? currentUser.username : 'Bilinmiyor'
                };
                await api.createUser(payload);
                logAction('Kullanıcı Oluşturma', `${formData.username} kullanıcısı oluşturuldu.`);
                alert('Kullanıcı başarıyla eklendi.');
            }
            closeModal();
            fetchUsers();
        } catch (error) {
            alert('İşlem sırasında hata oluştu: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleEditClick = (user) => {
        setFormData({
            username: user.username,
            password: '', // Şifre güvenliği için boş bırakıyoruz
            role: user.role,
            permissions: user.permissions || [],
            panelId: user.panelId || ''
        });
        setEditingUserId(user._id);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingUserId(null);
        setFormData({ username: '', password: '', role: 'user', permissions: [], panelId: '' });
    };

    const handleDelete = async (id) => {
        const userToDelete = users.find(u => u._id === id);
        if (userToDelete && userToDelete.createdBy === 'masteradmin' && !isManagement) {
            alert("Bu kullanıcıyı silme yetkiniz yok. Sadece Master Yönetim silebilir.");
            return;
        }
        if (window.confirm('Bu kullanıcıyı silmek istediğinize emin misiniz?')) {
            try {
                await api.deleteUser(id);
                logAction('Kullanıcı Silme', `Kullanıcı ID: ${id} silindi.`);
                fetchUsers();
            } catch (error) {
                alert('Silme işlemi başarısız: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const handle2FAClick = async (user) => {
        if (user.isTwoFactorEnabled) {
            if (window.confirm(`${user.username} kullanıcısı için 2FA özelliğini devre dışı bırakmak istediğinize emin misiniz?`)) {
                try {
                    await api.disable2FA(user._id);
                    logAction('2FA Devre Dışı', `${user.username} için 2FA kapatıldı.`);
                    alert('2FA başarıyla devre dışı bırakıldı.');
                    fetchUsers();
                } catch (error) {
                    alert('İşlem başarısız.');
                }
            }
            return;
        }
        try {
            const res = await api.setup2FA(user._id);
            setQrCode(res.data.qrCode);
            setCurrent2FAUser(user);
            setShow2FAModal(true);
            setTwoFactorToken('');
        } catch (error) {
            alert('QR Kod oluşturulamadı.');
        }
    };

    const handle2FAVerify = async (e) => {
        e.preventDefault();
        try {
            await api.verify2FA(current2FAUser._id, { token: twoFactorToken });
            logAction('2FA Aktifleştirme', `${current2FAUser.username} için 2FA açıldı.`);
            alert('2FA başarıyla aktifleştirildi.');
            setShow2FAModal(false);
            fetchUsers();
        } catch (error) {
            alert('Kod doğrulanamadı. Lütfen tekrar deneyin.');
        }
    };

    const getPanelDisplayName = (pId) => {
        if (!pId) return '-';
        const found = panels.find(p => p.panelId === pId);
        return found ? (found.name || found.panelId) : pId;
    };

    const activePanelName = isManagement
        ? (selectedPanelFilter ? getPanelDisplayName(selectedPanelFilter) : '')
        : '';

    const inputStyle = {
        width: '100%',
        padding: '8px',
        marginBottom: '10px',
        borderRadius: '4px',
        border: '1px solid var(--input-border)',
        boxSizing: 'border-box',
        backgroundColor: 'var(--input-bg)',
        color: 'var(--text-color)'
    };

    return (
        <div style={{ padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap' }}>
                    <h2 style={{ color: '#fff', margin: 0 }}>
                        Kullanıcılar {activePanelName ? `— ${activePanelName}` : ''}
                    </h2>
                    {isManagement && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <label style={{ color: '#aaa', fontSize: '0.9em', fontWeight: 'bold' }}>Departman:</label>
                            <select
                                value={selectedPanelFilter}
                                onChange={(e) => setSelectedPanelFilter(e.target.value)}
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid var(--input-border)',
                                    background: 'var(--input-bg)',
                                    color: 'var(--text-color)',
                                    fontWeight: 'bold'
                                }}
                            >
                                <option value="">Tüm Departmanlar</option>
                                {panels.map(p => (
                                    <option key={p._id} value={p.panelId}>{p.name || p.panelId}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>
                <button
                    onClick={openAddModal}
                    style={{ padding: '10px 20px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                    + Kullanıcı Ekle
                </button>
            </div>

            {/* Kullanıcı Tablosu */}
            <div style={{ backgroundColor: 'var(--card-bg)', borderRadius: '15px', boxShadow: 'var(--shadow)', overflow: 'hidden', padding: '20px' }}>
                <table cellPadding="15" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px', color: 'var(--text-color)' }}>
                    <thead>
                        <tr style={{ backgroundColor: 'var(--table-header-bg)', borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                            <th>Kullanıcı Adı</th>
                            {isManagement && <th>Departman</th>}
                            <th>Rol</th>
                            <th>Oluşturan</th>
                            <th>2FA</th>
                            <th style={{ textAlign: 'center' }}>İşlemler</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 ? (
                            <tr>
                                <td colSpan={isManagement ? 6 : 5} style={{ textAlign: 'center', padding: '20px', opacity: 0.6 }}>
                                    Kayıtlı kullanıcı bulunamadı.
                                </td>
                            </tr>
                        ) : (
                            users.map((user, index) => {
                                const rowBg = index % 2 === 0 ? 'var(--table-row-even)' : 'var(--table-row-odd)';
                                const radius = '15px';
                                return (
                                    <tr key={user._id}>
                                        <td style={{ backgroundColor: rowBg, borderTopLeftRadius: radius, borderBottomLeftRadius: radius, fontWeight: 'bold' }}>
                                            {user.username}
                                        </td>
                                        {isManagement && (
                                            <td style={{ backgroundColor: rowBg }}>
                                                <span style={{ padding: '4px 8px', borderRadius: '4px', backgroundColor: 'rgba(0,123,255,0.15)', color: '#60a5fa', fontWeight: 'bold', fontSize: '0.9em' }}>
                                                    {getPanelDisplayName(user.panelId)}
                                                </span>
                                            </td>
                                        )}
                                        <td style={{ backgroundColor: rowBg }}>
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                backgroundColor: user.role === 'admin' ? 'rgba(40,167,69,0.15)' : 'rgba(108,117,125,0.15)',
                                                color: user.role === 'admin' ? '#4ade80' : '#aaa',
                                                fontWeight: 'bold',
                                                fontSize: '0.85em'
                                            }}>
                                                {user.role === 'admin' ? 'Yönetici (Admin)' : 'Operatör (Kullanıcı)'}
                                            </span>
                                        </td>
                                        <td style={{ backgroundColor: rowBg }}>{user.createdBy || '-'}</td>
                                        <td style={{ backgroundColor: rowBg }}>
                                            <span style={{ color: user.isTwoFactorEnabled ? '#28a745' : '#dc3545', fontWeight: 'bold', fontSize: '0.85em' }}>
                                                {user.isTwoFactorEnabled ? '● Aktif' : '○ Pasif'}
                                            </span>
                                        </td>
                                        <td style={{ backgroundColor: rowBg, borderTopRightRadius: radius, borderBottomRightRadius: radius, textAlign: 'center' }}>
                                            <button onClick={() => handleEditClick(user)} style={{ marginRight: '10px', cursor: 'pointer', padding: '6px 12px', border: 'none', backgroundColor: '#007bff', color: '#fff', borderRadius: '4px', fontSize: '0.9em' }} title="Düzenle">
                                                Düzenle
                                            </button>
                                            <button
                                                onClick={() => handleDelete(user._id)}
                                                disabled={user.createdBy === 'master admin' && !isManagement}
                                                style={{
                                                    marginRight: '10px',
                                                    cursor: (user.createdBy === 'master admin' && !isManagement) ? 'not-allowed' : 'pointer',
                                                    padding: '6px 12px',
                                                    backgroundColor: (user.createdBy === 'master admin' && !isManagement) ? '#6c757d' : '#dc3545',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    color: '#fff',
                                                    fontSize: '0.9em'
                                                }}
                                            >
                                                Sil
                                            </button>
                                            <button onClick={() => handle2FAClick(user)} style={{ cursor: 'pointer', padding: '6px 12px', backgroundColor: user.isTwoFactorEnabled ? '#28a745' : '#17a2b8', border: 'none', borderRadius: '4px', color: '#fff', fontSize: '0.9em' }}>
                                                {user.isTwoFactorEnabled ? '2FA Kapat' : 'Google 2FA Kur'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {/* Kullanıcı Ekleme / Düzenleme Modalı */}
            {showModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto', color: 'var(--text-color)' }}>
                        <h3 style={{ marginTop: 0 }}>{editingUserId ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}</h3>
                        <form onSubmit={handleSubmit}>
                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Kullanıcı Adı *</label>
                            <input name="username" value={formData.username} onChange={handleInputChange} required style={inputStyle} />

                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>
                                Şifre {editingUserId ? <span style={{ fontWeight: 'normal', fontSize: '0.8em' }}>(Değiştirmek istemiyorsanız boş bırakın)</span> : '*'}
                            </label>
                            <input type="password" name="password" value={formData.password} onChange={handleInputChange} required={!editingUserId} style={inputStyle} />

                            {isManagement && (
                                <div style={{ marginBottom: '10px' }}>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Departman *</label>
                                    <select
                                        name="panelId"
                                        value={formData.panelId}
                                        onChange={handleInputChange}
                                        required
                                        style={inputStyle}
                                    >
                                        <option value="">Departman Seçiniz...</option>
                                        {panels.map(p => (
                                            <option key={p._id} value={p.panelId}>{p.name || p.panelId}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Rol</label>
                            <select name="role" value={formData.role} onChange={handleInputChange} style={inputStyle}>
                                <option value="user">Operatör (Kullanıcı)</option>
                                <option value="admin">Yönetici (Admin)</option>
                            </select>

                            {formData.role === 'user' && (
                                <div style={{ marginTop: '15px' }}>
                                    <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>Sayfa İzinleri</label>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                        {availablePages.map(page => (
                                            <label key={page.path} style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer', fontSize: '0.9em' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.includes(page.path)}
                                                    onChange={() => handlePermissionChange(page.path)}
                                                />
                                                {page.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div style={{ marginTop: '20px', textAlign: 'right' }}>
                                <button type="button" onClick={closeModal} style={{ marginRight: '10px', padding: '8px 16px', cursor: 'pointer', background: '#ccc', border: 'none', borderRadius: '4px' }}>İptal</button>
                                <button type="submit" style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>Kaydet</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* 2FA Modalı */}
            {show2FAModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
                }}>
                    <div style={{ background: 'var(--card-bg)', padding: '20px', borderRadius: '8px', width: '90%', maxWidth: '400px', textAlign: 'center', color: 'var(--text-color)' }}>
                        <h3>Google Authenticator Kurulumu</h3>
                        <p>Aşağıdaki QR kodu telefonunuzdaki Google Authenticator uygulaması ile taratın.</p>
                        {qrCode && <img src={qrCode} alt="QR Code" style={{ width: '200px', height: '200px', marginBottom: '15px' }} />}
                        <form onSubmit={handle2FAVerify}>
                            <input type="text" placeholder="Üretilen Kodu Girin" value={twoFactorToken} onChange={(e) => setTwoFactorToken(e.target.value)} style={{ ...inputStyle, textAlign: 'center', fontSize: '1.2em', letterSpacing: '5px' }} required />
                            <div style={{ marginTop: '20px' }}>
                                <button type="button" onClick={() => setShow2FAModal(false)} style={{ marginRight: '10px', padding: '8px 16px', cursor: 'pointer', background: '#ccc', border: 'none', borderRadius: '4px' }}>İptal</button>
                                <button type="submit" style={{ padding: '8px 16px', background: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Doğrula ve Aktifleştir</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPage;